import type { Grabber } from '../input/Grabbers';
import type { RoomHub } from '../net/roomHub';
import { bytesToBase64 } from '../net/base64';
import type {
  AssistantReplyDto,
  AssistantSpeechChunkDto,
  AssistantSpeechStartedDto,
  AssistantTurnResult,
} from '../net/wire';
import { AssistantSpeechPlayer } from './AssistantSpeechPlayer';
import { MicrophoneCapture } from './MicrophoneCapture';
import { PlaybackAmplitude } from './PlaybackAmplitude';

/**
 * The backstop for a stuck or lost-release button — plan/ai-in-room.md's original two-exit
 * design ("silence *or* a second press, both deliberately... a hard cap on turn length backs
 * both up") named this exact role for what was then `SilenceDetector`'s own hard cap. Push-to-
 * talk (owner, 2026-08-10: "push to talk is more natural... instead of a toggle before and
 * after speaking") replaces the *everyday* exit — silence detection *and* the second press —
 * with a plain, deterministic release, which is why `SilenceDetector` itself is gone entirely
 * (deleted rather than kept unused — it carried a real, independent bug; see below). A held
 * button whose release event never arrives (a controller that disconnects mid-turn, an event
 * lost the way `plan/ai-in-room.md`'s own tenancy-resolution notes describe losing other
 * things) is exactly the failure a hard cap exists for, so it survives on its own, moved here
 * now that release does the everyday closing rather than silence.
 *
 * **The deleted `SilenceDetector` had its own bug, worth recording rather than silently
 * dropping with the class.** `PlaybackAmplitude.start` documents, and `.resume()`s, the exact
 * hazard CLAUDE.md's own "A freshly-constructed AudioContext starts suspended" entry names —
 * a suspended context accepts `start()`, plays nothing, and (for `AnalyserNode`, the mechanism
 * both classes shared) reads back all-zero samples rather than the real signal. `SilenceDetector
 * .start()` built an identical `AudioContext`/`AnalyserNode` pair and never called `.resume()`
 * at all. A suspended context there would have read the microphone as permanently silent —
 * `SilenceTimer.hasSpoken` never flips true, so the close-on-silence clock never even arms —
 * which would present exactly as "the turn never ends on its own," indistinguishable from the
 * hard cap being the only way out. Never confirmed as the mechanism behind the reported bug
 * this change fixes (no headset instrumentation ever isolated it, and the reported symptom —
 * "pressing the button doesn't enable the microphone" — is also consistent with simply not
 * having tried push-to-talk's deterministic release yet), but a real, independently-confirmed
 * defect regardless, and one push-to-talk removes for good rather than leaving to be found
 * again on a class nothing calls any more.
 */
const HARD_CAP_MS = 30_000;

/** One queued arrival waiting for its turn to play — either a complete clip
 *  (`AssistantReplyDto`, the unary path) or a streamed utterance already announced by
 *  `assistantSpeechStarted` (the streaming path — see `AssistantSpeechPlayer`'s own module
 *  doc for why the mechanism differs while the queue shape does not). `playNext` is the one
 *  place that reads which kind an entry is. */
type PendingUtterance =
  | { kind: 'clip'; reply: AssistantReplyDto }
  | { kind: 'stream'; started: AssistantSpeechStartedDto };

/**
 * Owns the assistant's client-side state machine: Y toggles a persistent microphone
 * preference, X is push-to-talk on top of it — held down opens a turn, released ends it — and
 * every visible or audible consequence — the controller's own mic indicator, and (via
 * `onGlowChanged`) the room's ceiling — derives from `capturing`, never from `micEnabled` read
 * on its own. That is the whole invariant plan/ai-in-room.md calls out: "Deriving them from
 * micEnabled instead is the bug this note exists to prevent." Getting it right is what makes
 * X's override of a muted microphone free — it needs no branch of its own, only this one
 * derived getter. It also means the room hears the question, not only the answer, whenever
 * `micEnabled` was already true — and, while a turn is held open, even when it was not: the
 * override does not create a private channel to the assistant, it only ever changes whose
 * decision it was for the microphone to be live right now (plan/ai-in-room.md, "Everyone in
 * the room hears the answer" — a private question whose answer is broadcast to everyone would
 * leave the room hearing a reply to something it never heard asked).
 *
 * No Scene or Mesh dependency, on purpose, the same separation `RoomState`/`RoomView` and
 * `ToolMode`/`ToolPropRenderer` already keep elsewhere in this project: this class is the
 * state, a caller in main.ts wires a renderer to the callbacks below. That is also what
 * makes the state machine itself testable with no Babylon scene at all.
 *
 * `onAmplitudeChanged` is the one exception to "no Scene dependency" in spirit rather than in
 * fact: it carries no Babylon type, only a plain number, but it exists purely so a renderer
 * (`SceneRoot.setAssistantAmplitude`) can animate something visual — this class has no opinion
 * about what. `playback` (a `PlaybackAmplitude`) is the WebAudio plumbing that produces it, by
 * analysing the reply clip's own output while it plays; see that class's own module doc for
 * why it is a separate class rather than inlined here.
 */

export type MicIndicatorState = 'live' | 'muted' | 'unavailable';

/**
 * Room-wide, not per-person — plan/ai-in-room.md: "blue holds through the thinking gap...
 * green means the answer is here." `speaking` is driven by the reply clip's own `playing`
 * event, not by the moment it arrives, for the same reason the microphone indicator follows
 * actual capture rather than a toggle: a decode/buffering gap between "the clip arrived"
 * and "it is audible" would otherwise show green before anyone can hear anything.
 *
 * `listening` and `listening-remote` are the same fact — a turn is open — seen from two
 * different seats. A two-person headset session reported this as one bug: "the Gemma input
 * indicator enables for all players so the channel to her is wide open for everyone", because
 * both the asker and everyone else in the room saw the identical glow for a turn that only one
 * of them actually holds. The room-wide visibility itself is correct and deliberate (Gemma
 * hears and answers the whole room, DESIGN.md, "The in-room assistant") — the defect was that
 * one visual meant two different things. `listening` is only ever set from this connection's
 * own `beginTalk` (this is *your* turn); `listening-remote` is only ever set from the
 * `assistantTurnStarted` broadcast, which the server sends to `OthersInGroup` and therefore
 * never to the asker themselves (`RoomSyncHub.BeginAssistantTurn`) — so the two call sites
 * below are already structurally disjoint and need no peer-id plumbing to tell apart which is
 * which; see this class's own top-level doc for why no Scene/peer-id dependency was added here.
 * `speaking` stays a single shared state for both seats: once a reply is playing, everyone in
 * the room is doing the same thing — listening to Gemma's answer — so there is only one fact
 * left to show.
 */
export type AssistantGlow = 'none' | 'listening' | 'listening-remote' | 'speaking';

// spec: SPEC-102
export class AssistantController {
  /**
   * Y's persistent base state — what capture reverts to once a held-X turn ends.
   *
   * Starts `true`, deliberately: the owner has to remember to reach for the off-hand toggle
   * on *every* session if this defaults off, and a session that nobody talks to the
   * assistant in because they forgot is a worse failure than one that starts listening.
   * `configure()` is what actually turns the microphone on to match, once permission and
   * availability are both known — see its own doc comment for the one condition that gates
   * it, and `plan/ai-in-room.md`, "Microphone defaults to on" for why this is safe only
   * because the product is single-tenant and invite-only today.
   */
  private micEnabled = true;

  /** True only while *this* connection's own turn is open — which, since push-to-talk, means
   *  only while the off-hand talk button is physically held down; see `beginTalk`/`endTalk`. */
  private myTurnCapturing = false;

  /** Microphone permission granted *and* the room reports an assistant configured. Set
   *  once, after join — see `configure`. */
  private available = false;

  /**
   * Set once, after join — see `configure`. The server already refuses `BeginAssistantTurn`
   * for a spectator actor (`RoomSyncHub.BeginAssistantTurn`, `151f734`), so this is a client-side
   * courtesy, not the enforcement: `beginTalk` below stays inert rather than making a request
   * that is only ever going to be refused. Player-to-player voice (`micEnabled`/`toggleMic`,
   * `capturing`) is untouched by this flag — plan/roadmap.md's Phase 3 is explicit that voice
   * "serves the VR users equally," spectators included.
   */
  private spectator = false;

  /** Armed for the duration of an open turn, cleared the moment it closes any way at all
   *  (release, refusal, a page refresh) — see `armHardCap`/`disarmHardCap`. */
  private hardCapHandle: ReturnType<typeof setTimeout> | null = null;

  /**
   * True once this connection's own persistent voice-relay listener is registered with
   * `capture` — tracked here rather than read off `capture.isCapturing`, because
   * `MicrophoneCapture` is a shared, multi-subscriber instance (`NodeLabelController`'s label
   * capture is another subscriber, wired to the same instance in `main.ts`, and this
   * connection's own turn audio — `onTurnChunk` below — is now a third, independent
   * subscription): `capture.isCapturing` reports whether *anything* is keeping the hardware
   * hot, not whether *this particular* subscription has registered. Reading that shared flag
   * here would make `syncCaptureHardware` skip `capture.start()` — and never register
   * `onVoiceChunk` at all — whenever another subscriber alone had already opened the
   * microphone.
   */
  private capturingHardware = false;

  /**
   * The one stable function reference this connection's persistent voice-relay subscription
   * uses for both `capture.start()` and `capture.stop()` — `MicrophoneCapture` identifies a
   * subscriber by that reference (see its own module doc), so passing a fresh arrow function
   * to each call would look like an ever-growing set of distinct subscribers that never
   * unsubscribe. Open whenever `capturing` is true (Y, an open turn, or both) and relays every
   * chunk to the room via `streamVoiceChunk` — see `capturing`'s own doc for why a turn holds
   * this open even when Y itself is off.
   */
  private readonly onVoiceChunk = (bytes: Uint8Array): void => {
    this.hub.streamVoiceChunk({ data: bytesToBase64(bytes) });
  };

  /**
   * The stable reference for this connection's own turn-audio subscription — a second,
   * independent `MicrophoneCapture` subscription from `onVoiceChunk` above, opened only for
   * the lifetime of an open turn (`beginTalk`/`endTalk`) and routed exclusively to
   * `streamAssistantAudioChunk`.
   *
   * <p>A dedicated subscription, not a re-routing of whatever `onVoiceChunk` already
   * receives, because a turn needs its *own* decodable stream regardless of whether the
   * room's persistent voice relay (Y) was already running when the turn opened. A live,
   * credentialed run against Gemini's transcription endpoint (documented on
   * `MicrophoneCapture`'s own module doc) showed that reusing a single already-running
   * recorder's chunks for a second, later-starting logical stream — even with its first
   * chunk retained and prepended as a stand-in header — is not reliably decodable: a real
   * `MediaRecorder`'s first `ondataavailable` event is not guaranteed to be the complete WebM
   * header on its own. `MicrophoneCapture.start` now hands every subscriber its own
   * `MediaRecorder` against the shared device instead, so this subscription's own stream is
   * guaranteed to start with its own genuine header the instant it opens — turn-start no
   * longer has to reason about whether the hardware was already hot.</p>
   */
  private readonly onTurnChunk = (bytes: Uint8Array): void => {
    this.hub.streamAssistantAudioChunk({ data: bytesToBase64(bytes) });
  };

  private mimeType = '';
  private lastIndicator: MicIndicatorState = 'unavailable';
  private currentAudio: HTMLAudioElement | null = null;

  /** Whether a streamed utterance (via `speech`) is the thing currently playing — the
   *  streaming-path equivalent of `currentAudio !== null` for the classic clip path. Exactly
   *  one of the two is ever true at once; see `playNext`. */
  private streamPlaying = false;

  /** Utterances that have arrived but are not yet playing — see `handleReply`/
   *  `handleSpeechStarted`/`playNext`. Almost never more than one entry long in practice
   *  (today's pipeline sends at most two utterances per turn), but nothing here assumes
   *  that: a longer narrated sequence is a longer queue, not a different mechanism. Mixes
   *  both kinds of arrival (`PendingUtterance`) because the two delivery paths still have to
   *  play in one strict, never-overlapping order — the server always fully drains a streamed
   *  utterance's own chunks before touching any other utterance's broadcast, so this queue
   *  never needs to interleave a stream's chunks with a clip arriving mid-utterance. */
  private readonly pending: PendingUtterance[] = [];

  /** Whether the clip currently playing (or the one that most recently finished, once the
   *  queue is briefly empty) said there was more still coming. Read only when `pending` runs
   *  dry — see `playNext` — so the ceiling can hold through the gap between an
   *  acknowledgement ending and a confirmation not having arrived yet, the same way it
   *  already holds through the "thinking" gap before the first clip of a turn ever arrives,
   *  rather than flickering off and back on for every tool a turn happens to run. */
  private expectingMoreUtterances = false;

  /** Diagnostics only — how many chunks the current streamed utterance has received, so the
   *  console line for the first one can be distinguished from the rest. Reset per utterance. */
  private chunkCount = 0;

  onIndicatorChanged: ((state: MicIndicatorState) => void) | null = null;
  onGlowChanged: ((state: AssistantGlow) => void) | null = null;

  /** The reply clip's own playback amplitude (RMS, 0..1), sampled by `playback` while a clip
   *  is actually audible — see `PlaybackAmplitude`'s own module doc for the mechanism and its
   *  one real gotcha. Fires only between a clip's `playing` event and its end; nothing calls
   *  this while 'listening' or 'none', so a consumer (`SceneRoot.setAssistantAmplitude`) that
   *  ignores the glow state entirely still cannot animate the wrong thing — there is simply
   *  nothing sent outside 'speaking'. */
  onAmplitudeChanged: ((level: number) => void) | null = null;

  onNotice: ((message: string) => void) | null = null;

  constructor(
    private readonly hub: RoomHub,
    private readonly capture: MicrophoneCapture = new MicrophoneCapture(),
    private readonly playback: PlaybackAmplitude = new PlaybackAmplitude(),
    private readonly speech: AssistantSpeechPlayer = new AssistantSpeechPlayer(),
  ) {
    // OthersInGroup-only (RoomSyncHub.BeginAssistantTurn) — this never reaches the asker's own
    // connection, so every receiver here is, by construction, someone else's turn. That is
    // exactly why 'listening-remote' rather than 'listening' belongs here: see AssistantGlow's
    // own doc comment for the full reasoning and why no peer-id plumbing was needed to draw
    // this line. beginTalk()'s local setGlow('listening') below is the other, disjoint half.
    hub.on('assistantTurnStarted', () => this.setGlow('listening-remote'));
    hub.on('assistantReply', (reply) => this.handleReply(reply));
    hub.on('assistantSpeechStarted', (started) => this.handleSpeechStarted(started));
    hub.on('assistantSpeechChunk', (chunk) => this.handleSpeechChunk(chunk));
    hub.on('assistantSpeechEnded', (utteranceIndex) => {
      console.info(`[assistant] speechEnded utterance=${utteranceIndex}`);
      this.speech.end(utteranceIndex);
    });
    hub.on('assistantTurnFailed', () => {
      // The one place besides a clip's own natural end that can close a turn out — a
      // pipeline failure part-way through its own ordered sequence (plan/ai-in-room.md,
      // "Speak, then act, then speak"). Reset regardless of whatever is still queued or
      // playing: whatever was already heard stays heard, this only stops the ceiling from
      // waiting on an utterance that is now never coming.
      this.expectingMoreUtterances = false;
      this.setGlow('none');
    });
  }

  /**
   * Whether audio is being captured *this instant*, for any reason — Y, the talk button
   * physically held down, or both. The one flag every visible or audible consequence has to
   * derive from; see this class's own top-level doc.
   */
  get capturing(): boolean {
    return this.micEnabled || this.myTurnCapturing;
  }

  /**
   * Call once, after `hub.join()` resolves and the splash's own microphone gesture has
   * settled — both are known only then. `assistantAvailable` comes off the room snapshot;
   * it is resolved once at server startup and does not change mid-session, so this is not
   * re-checked later. `isSpectator` defaults to `false` so every existing caller (every test
   * in this file, main.ts before the spectator role existed) keeps behaving exactly as before —
   * see `spectator`'s own doc for what it changes.
   */
  configure(micPermissionGranted: boolean, assistantAvailable: boolean, isSpectator = false): void {
    this.available = micPermissionGranted && assistantAvailable;
    this.spectator = isSpectator;
    this.mimeType = MicrophoneCapture.pickMimeType();

    // Synchronous, before the hardware is ever touched: the indicator has to be honest about
    // the default the instant this call returns, not only after the first toggle — a caller
    // wiring onIndicatorChanged before this call sees 'live' immediately when the mic is on
    // by default and available.
    this.reportIndicator();

    // Actually open the microphone to match the default above. Gated on `available`, not
    // just `capturing`: without a granted permission there is no hardware to start, and
    // attempting it would either silently fail or re-prompt for a permission the splash
    // screen already resolved once. `syncCaptureHardware` itself will correct the indicator
    // (back to 'muted') if the hardware genuinely fails to open — same fail-soft path
    // toggleMic already relies on.
    if (this.available && this.capturing) {
      void this.syncCaptureHardware();
    }
  }

  /**
   * Y: toggles the persistent base state. A no-op, deliberately silent, when unavailable —
   * there is nothing to explain that pressing it again would fix.
   *
   * Returns a `Promise` purely so a test can await the hardware sync it kicks off
   * deterministically; every real caller (the per-frame `tick` below, DevSim's keydown
   * handler) fires it without awaiting, the same `void controller.toggleMic()` shape
   * `AuthoringController.runAction` already uses for its own tray actions.
   */
  async toggleMic(): Promise<void> {
    if (!this.available) {
      return;
    }

    this.micEnabled = !this.micEnabled;
    this.reportIndicator();
    await this.syncCaptureHardware();
  }

  /**
   * X, pressed down: opens a turn. Push-to-talk (owner, 2026-08-10: "push to talk is more
   * natural for Gemma, instead of a toggle before and after speaking") — there is no second
   * press that closes an open turn any more; `endTalk` below, fired when the same button is
   * *released*, is the only way this connection's own turn ends early, backed up by
   * `HARD_CAP_MS` for a release that never arrives. Silently does nothing while unavailable,
   * while someone else's turn is open, while this connection is a spectator (see `spectator`'s
   * own doc — the server would refuse the same request, so this does not even ask), or while a
   * turn of this connection's own is already open (a held button re-firing `justPressed` cannot
   * happen in practice, but a defensive no-op costs nothing and avoids a second, overlapping
   * `beginAssistantTurn` call if it ever did); the `!available` refusal carries a reason through
   * `onNotice`, but a spectator gets no notice either, the same silent-no-op shape `toggleMic`
   * already uses when there is nothing pressing it again would fix.
   */
  async beginTalk(): Promise<void> {
    if (this.spectator) {
      return;
    }

    if (!this.available) {
      this.onNotice?.('no assistant configured');
      return;
    }

    if (this.myTurnCapturing) {
      return;
    }

    let result: AssistantTurnResult;

    try {
      result = await this.hub.beginAssistantTurn(this.mimeType);
    } catch (error) {
      this.onNotice?.(error instanceof Error ? error.message : String(error));
      return;
    }

    if (!result.granted) {
      this.onNotice?.(
        result.reason === 'already-open'
          ? 'someone else is already talking to the assistant'
          : 'no assistant configured',
      );
      return;
    }

    // Applied locally: BeginAssistantTurn notifies OthersInGroup, not this connection — see
    // roomHub.ts's own remarks on this method, and CLAUDE.md, "A hub method that returns
    // state does not broadcast it back to you."
    this.setGlow('listening');

    // A dedicated subscription for this turn's own audio, opened fresh regardless of
    // whether the persistent voice relay (`onVoiceChunk`) is already running — see
    // `onTurnChunk`'s own doc for why this, not a retained/prepended header off an
    // already-running recorder, is what makes this turn's stream reliably decodable. If the
    // device itself cannot open (a permission or hardware failure — `available` being true
    // only means permission was granted *once*, at join), there is nothing to send this turn
    // at all, so it is abandoned honestly rather than opened with no audio behind it.
    try {
      await this.capture.start(this.mimeType, this.onTurnChunk);
    } catch (error) {
      console.warn('Assistant: failed to start microphone capture for this turn.', error);
      this.onNotice?.('microphone unavailable');
      this.setGlow('none');
      void this.hub.endAssistantTurn().catch(() => {});
      return;
    }

    this.myTurnCapturing = true;
    this.reportIndicator();

    // Ensures the persistent voice-relay subscription is open too — `capturing` now includes
    // `myTurnCapturing`, so the room hears the question even if Y itself was off; see
    // `capturing`'s own doc.
    await this.syncCaptureHardware();

    this.armHardCap();
  }

  /**
   * X, released — or the hard cap firing (`armHardCap`): closes this connection's own open
   * turn, if any. A no-op if none is open: a release can reach here with nothing open when a
   * turn was refused (`beginTalk` returned early) but the button is only let go afterward, or
   * when the hard cap has already closed it and the physical release follows later.
   */
  async endTalk(): Promise<void> {
    if (!this.myTurnCapturing) {
      return;
    }

    this.disarmHardCap();
    this.myTurnCapturing = false;
    this.capture.stop(this.onTurnChunk);
    this.reportIndicator();
    await this.syncCaptureHardware();

    try {
      await this.hub.endAssistantTurn();
    } catch (error) {
      this.onNotice?.(error instanceof Error ? error.message : String(error));
    }
  }

  /**
   * Read once per frame from the off-hand grabber — the same poll-based shape every other
   * gesture in this project already uses (Grabbers.ts: "everything else here is a snapshot
   * rather than an event"). `talkButton`/`micToggleButton` are null for hands and the
   * desktop pointer; DevSim calls `beginTalk`/`endTalk`/`toggleMic` directly instead,
   * bypassing this, for desktop testability — see its own module doc.
   */
  tick(offHand: Grabber | undefined): void {
    if (offHand?.micToggleButton?.justPressed) {
      void this.toggleMic();
    }

    if (offHand?.talkButton?.justPressed) {
      void this.beginTalk();
    }

    if (offHand?.talkButton?.justReleased) {
      void this.endTalk();
    }
  }

  /** Drops anything held, so a page refresh does not leave the room's channel open. */
  releaseImmediately(): void {
    this.disarmHardCap();

    // Unsubscribes both of this connection's own listeners — a harmless no-op via `stop`'s
    // own "not currently subscribed" case for whichever one (or both) never actually opened
    // this session. `NodeLabelController.releaseImmediately`, called alongside this one from
    // the same `beforeunload` handler (main.ts), does the identical thing for its own
    // listener on the same shared instance — between the three subscriptions, the hardware
    // itself closes once all of them are gone, regardless of which runs first.
    this.capture.stop(this.onVoiceChunk);
    this.capture.stop(this.onTurnChunk);
    this.capturingHardware = false;
    this.myTurnCapturing = false;
    this.speech.releaseAll();

    // Belt-and-suspenders alongside the disconnect this is called right before
    // (main.ts's beforeunload also calls hub.stop()): other peers' own PeerLeft handling
    // already cleans up their playback for this connection regardless, but an explicit
    // close here is a fire-and-forget no-op if it never lands, and correct if it does.
    this.hub.endVoiceStream();
  }

  /** Arms the hard-cap backstop — see this file's own top-level doc for why it still exists
   *  once release does the everyday closing. Always disarms first, so a caller never has to
   *  worry about a stale timer from an earlier turn outliving this one. */
  private armHardCap(): void {
    this.disarmHardCap();
    this.hardCapHandle = setTimeout(() => {
      this.hardCapHandle = null;
      this.onNotice?.('assistant stopped listening — reached the time limit');
      void this.endTalk();
    }, HARD_CAP_MS);
  }

  private disarmHardCap(): void {
    if (this.hardCapHandle !== null) {
      clearTimeout(this.hardCapHandle);
      this.hardCapHandle = null;
    }
  }

  /** Subscribes or unsubscribes this connection's own persistent voice-relay listener on the
   *  shared capture device to match `capturing` — see `capturingHardware`'s own doc for why
   *  that, not `capture.isCapturing`, is what gates this. Never touches `onTurnChunk`, the
   *  independent turn-audio subscription `beginTalk`/`endTalk` own directly. */
  private async syncCaptureHardware(): Promise<void> {
    if (this.capturing && !this.capturingHardware) {
      try {
        await this.capture.start(this.mimeType, this.onVoiceChunk);
        this.capturingHardware = true;

        // Declares this stream's container/codec to the room before any chunk of it can
        // possibly arrive — the first chunk is CHUNK_INTERVAL_MS away at the earliest —
        // so every other peer's playback pipeline has what it needs to stand up a fresh
        // MediaSource/SourceBuffer pair before the audio itself shows up. Fired for *any*
        // reason capture just started, Y or an open assistant turn or both: this is the
        // room hearing a live microphone, not specifically "the assistant is listening"
        // (plan/ai-in-room.md, "Player-to-player voice").
        this.hub.startVoiceStream(this.mimeType);
      } catch (error) {
        console.warn('Assistant: failed to start microphone capture.', error);

        // Whatever asked for capture did not get it — reflect that honestly rather than
        // reporting "live" for a microphone that never actually opened. If a turn's own
        // dedicated subscription (`onTurnChunk`) had already opened successfully moments
        // earlier (`beginTalk`), it is stopped here too, rather than left dangling open with
        // nowhere honest left to report it — the room never heard `startVoiceStream` for
        // this failed attempt, so there is nothing for a live turn to correctly attach to.
        if (this.myTurnCapturing) {
          this.capture.stop(this.onTurnChunk);
        }

        this.micEnabled = false;
        this.myTurnCapturing = false;
        this.reportIndicator();
      }

      return;
    }

    if (!this.capturing && this.capturingHardware) {
      this.capture.stop(this.onVoiceChunk);
      this.capturingHardware = false;
      this.hub.endVoiceStream();
    }
  }

  /**
   * Enqueues one utterance of the current turn's own ordered sequence
   * (plan/ai-in-room.md, "Speak, then act, then speak") rather than playing it immediately:
   * a second utterance arriving while the first is still audible must wait behind it, never
   * cut it off or overlap it — the whole point of the fix this class is half of. Starts the
   * queue draining only if nothing was already playing; otherwise `playNext` reaches this
   * entry in its own turn, once whatever came before it finishes.
   */
  private handleReply(reply: AssistantReplyDto): void {
    this.pending.push({ kind: 'clip', reply });
    this.maybeStartNext();
  }

  /**
   * A streamed utterance's own arrival — the streaming counterpart to `handleReply`, queued
   * the same way and for the same reason. `speech.start` is called immediately, not deferred
   * until this entry reaches the head of `pending`: chunks (`handleSpeechChunk`, below) can
   * legitimately arrive while a previous turn's clip is still finishing playback, and
   * `AssistantSpeechPlayer` buffers them internally until `play()` is actually called for
   * this index — see that class's own remarks on `start()`/`play()`.
   */
  private handleSpeechStarted(started: AssistantSpeechStartedDto): void {
    // Deliberately console.info rather than a debug flag: the streaming speech path broke twice
    // in production in ways no automated check here could see (CLAUDE.md, "A freshly-constructed
    // AudioContext starts suspended"), and the browser console on a headset is the only place a
    // diagnosis can come from. One line per seam, so a transcript says exactly how far a turn got.
    console.info(
      `[assistant] speechStarted utterance=${started.utteranceIndex} ` +
        `sampleRate=${started.sampleRateHertz} isFinal=${started.isFinal} ` +
        `queued=${this.pending.length} currentAudio=${this.currentAudio !== null} ` +
        `streamPlaying=${this.streamPlaying}`,
    );
    this.speech.start(started.utteranceIndex, started.sampleRateHertz);
    this.pending.push({ kind: 'stream', started });
    this.maybeStartNext();
  }

  /** Routed straight through to `speech` regardless of this queue's own state — see
   *  `handleSpeechStarted`'s own remarks for why that is safe. */
  private handleSpeechChunk(chunk: AssistantSpeechChunkDto): void {
    this.chunkCount++;

    // Only the first chunk of each utterance, not all of them: a per-chunk line would bury the
    // seams either side of it, which are the ones that matter.
    if (this.chunkCount === 1) {
      console.info(
        `[assistant] first speech chunk utterance=${chunk.utteranceIndex} ` +
          `bytes(base64)=${chunk.data.length}`,
      );
    }

    this.speech.appendChunk(chunk.utteranceIndex, chunk.data);
  }

  private maybeStartNext(): void {
    if (this.currentAudio === null && !this.streamPlaying) {
      this.playNext();
    }
  }

  /**
   * Dequeues and plays the next utterance, or — if `pending` is empty — either holds the
   * ceiling where it is (more of this turn is still coming; the gap between an
   * acknowledgement ending and the confirmation not having arrived yet, typically while a
   * tool runs server-side) or turns it off (the last thing played was genuinely this turn's
   * final utterance, or `assistantTurnFailed` already forced the issue). Called once from
   * `handleReply`/`handleSpeechStarted` when nothing was already playing, and once more from
   * every utterance's own end (`finish()` below for a clip, `playStream`'s own `onEnded` for
   * a stream) — so this is the one place an utterance actually starts, and the one place the
   * queue's own emptiness is read.
   */
  private playNext(): void {
    const item = this.pending.shift();

    if (item === undefined) {
      if (!this.expectingMoreUtterances) {
        this.setGlow('none');
      }

      return;
    }

    if (item.kind === 'stream') {
      this.playStream(item.started);
      return;
    }

    this.playClip(item.reply);
  }

  /**
   * Plays a streamed utterance already buffering in `speech` (see `handleSpeechStarted`).
   * `onStarted`/`onEnded` mirror `playClip`'s own `playing`/`finish` handling below — same
   * "observable state follows real state" glow rule, same "advance the queue exactly once,
   * from the one place an utterance actually starts" shape — just driven by
   * `AssistantSpeechPlayer`'s own callbacks instead of `<audio>` events, since a scheduled
   * `AudioBufferSourceNode` has neither.
   *
   * No amplitude tracking on this path today, unlike `playClip`'s `playback.start` — the
   * visor's live voice visualisation (`SceneRoot.setAssistantAmplitude`) only ever averages
   * numbers already computed elsewhere and has no dependency of its own on which playback
   * mechanism is used, so wiring it up here is a follow-on rather than something this change
   * needed for its own correctness; `onAmplitudeChanged` simply is not called while a
   * streamed utterance is the one playing.
   */
  private playStream(started: AssistantSpeechStartedDto): void {
    this.expectingMoreUtterances = !started.isFinal;
    this.streamPlaying = true;

    console.info(`[assistant] playStream utterance=${started.utteranceIndex}`);

    this.speech.play(
      started.utteranceIndex,
      () => {
        console.info(`[assistant] stream audible utterance=${started.utteranceIndex}`);
        this.setGlow('speaking');
      },
      () => {
        console.info(`[assistant] stream ended utterance=${started.utteranceIndex}`);
        this.streamPlaying = false;
        this.chunkCount = 0;
        this.playNext();
      },
    );
  }

  private playClip(reply: AssistantReplyDto): void {
    this.expectingMoreUtterances = !reply.isFinal;

    const audio = new Audio(`data:${reply.audioMimeType};base64,${reply.audio}`);
    this.currentAudio = audio;

    // Every handler below re-checks this rather than assuming that because *it* is the one
    // firing, its own clip is still the current one. Nothing here ever supersedes a clip
    // still playing — arrivals only ever queue behind it — but a clip's own `ended`/`error`
    // can still both fire for the same element in a browser quirk, and the second of the two
    // must not re-advance the queue a second time. Without this guard that would show up as
    // the ceiling or the amplitude callback reacting to a clip nothing is playing any more.
    const isStillCurrent = () => this.currentAudio === audio;

    // `playing` rather than the moment `.play()` is called: a decode or buffering gap
    // would otherwise show the ceiling green before the clip is actually audible — the
    // same "observable state follows real state" rule the microphone indicator already
    // follows for capture. Amplitude tracking starts on the same event and for the same
    // reason: nothing is audible to track before this fires either.
    audio.addEventListener(
      'playing',
      () => {
        if (isStillCurrent()) {
          this.setGlow('speaking');
          this.playback.start(audio, (level) => {
            if (isStillCurrent()) {
              this.onAmplitudeChanged?.(level);
            }
          });
        }
      },
      { once: true },
    );

    const finish = () => {
      if (isStillCurrent()) {
        this.currentAudio = null;
        this.playback.stop();

        // Whatever is queued next for this turn (or the next), or 'none'/'held' as decided
        // above — never unconditionally 'none' here, or a two-utterance turn would flicker
        // the ceiling off between its acknowledgement and its confirmation.
        this.playNext();
      }
    };

    audio.addEventListener('ended', finish, { once: true });
    audio.addEventListener('error', finish, { once: true });

    void audio.play().catch((error: unknown) => {
      console.warn('Assistant: failed to play the reply clip.', error);
      finish();
    });
  }

  private setGlow(glow: AssistantGlow): void {
    this.onGlowChanged?.(glow);
  }

  private reportIndicator(): void {
    const next: MicIndicatorState = !this.available ? 'unavailable' : this.capturing ? 'live' : 'muted';

    if (next === this.lastIndicator) {
      return;
    }

    this.lastIndicator = next;
    this.onIndicatorChanged?.(next);
  }
}
